import { Link } from "@tanstack/react-router";
import { useState, type ReactNode } from "react";
import { ChevronDown } from "lucide-react";
import chart from "@/assets/public/gulf-chart.jpg";
import logoImg from "@/assets/maven-scouts-logo.jpg";
const logo = { url: logoImg };
import scout from "@/assets/public/scout-binos.png";

const nav = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/dual-use-accelerator", label: "Dual Use Accelerator" },
  { to: "/partners", label: "Ecosystem Partners" },
  { to: "/events", label: "Events" },
  { to: "/newsletter", label: "Newsletter" },
  { to: "/shop", label: "Shop" },
] as const;

export function Header() {
  const [open, setOpen] = useState(false);
  return (
    <header className="sticky top-0 z-50 border-b border-border bg-background/95 shadow-sm backdrop-blur">
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between gap-2 px-3 sm:gap-3 sm:px-6">
        <Link to="/" className="flex shrink-0 items-center">
          <img src={logo.url} alt="Maven Scouts — Bold, Vigilant, Innovative" className="h-8 w-auto sm:h-14" />
        </Link>
        <div className="flex flex-1 items-center justify-start gap-2 lg:hidden">
          <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} aria-haspopup="true" className="inline-flex shrink-0 items-center gap-1 border border-highlight px-2 py-2 font-mono text-xs uppercase tracking-widest text-highlight hover:bg-tint sm:gap-2 sm:px-3">
            Menu <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
          </button>
        </div>
        <nav className="hidden items-center gap-3 xl:gap-6 lg:flex">
          {nav.map((n) => (
            <Link key={n.to} to={n.to} className="font-mono text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground" activeOptions={{ exact: true }} activeProps={{ className: "!text-highlight" }}>
              {n.label}
            </Link>
          ))}
        </nav>
        <div className="flex items-center gap-2 sm:gap-3">
          <a href="https://gulfsouthdefense.com" target="_blank" rel="noopener noreferrer" className="hidden shrink-0 whitespace-nowrap sm:inline-block border border-highlight px-4 py-2 font-mono text-xs uppercase tracking-widest text-highlight hover:bg-tint">
            GSDTC Website
          </a>
          <Link to="/login" className="shrink-0 whitespace-nowrap border border-highlight bg-primary px-2 py-2 sm:px-4 font-mono text-xs uppercase tracking-widest text-primary-foreground hover:bg-secondary">
            Client Login
          </Link>
        </div>
      </div>
      {open && (
        <nav className="border-t border-border bg-background px-6 py-3 lg:hidden">
          {nav.map((n) => (
            <Link key={n.to} to={n.to} onClick={() => setOpen(false)} className="block py-3 font-mono text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground" activeOptions={{ exact: true }} activeProps={{ className: "!text-highlight" }}>
              {n.label}
            </Link>
          ))}
          <a href="https://gulfsouthdefense.com" target="_blank" rel="noopener noreferrer" onClick={() => setOpen(false)} className="block py-3 font-mono text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground sm:hidden">GSDTC Website</a>
        </nav>
      )}
    </header>
  );
}

export function NewsletterForm() {
  const [done, setDone] = useState(false);
  if (done) return <p className="font-mono text-xs uppercase tracking-widest text-primary">You're subscribed — thank you.</p>;
  return (
    <form onSubmit={(e) => { e.preventDefault(); setDone(true); }} className="flex flex-col gap-3 sm:flex-row">
      <input required type="email" placeholder="you@organization.com" className="flex-1 border border-border bg-background px-4 py-3 text-sm outline-none focus:border-primary" />
      <button className="bg-primary px-6 py-3 font-mono text-xs uppercase tracking-widest text-primary-foreground hover:bg-secondary">Subscribe</button>
    </form>
  );
}

export function Footer() {
  return (
    <footer className="border-t border-border bg-surface">
      <div className="mx-auto grid max-w-7xl gap-6 border-b border-border px-6 py-12 md:grid-cols-2 md:items-center">
        <div className="flex items-center gap-6">
          <img src={scout} alt="" aria-hidden="true" className="h-28 w-28 shrink-0 object-cover mix-blend-multiply" />
          <div>
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-primary">Newsletter</p>
          <h3 className="mt-2 font-display text-2xl uppercase">Get The Scout Report</h3>
          </div>
        </div>
        <NewsletterForm />
      </div>
      <div className="mx-auto flex max-w-7xl flex-col gap-4 px-6 py-10 font-mono text-xs uppercase tracking-widest text-muted-foreground md:flex-row md:justify-between">
        <div className="flex flex-col gap-3 normal-case"><img src={logo.url} alt="Maven Scouts" className="h-12 w-auto self-start" /><span className="uppercase">© {new Date().getFullYear()} Maven Scouts</span></div>
        <a href="https://www.linkedin.com/company/mavenscouts/" target="_blank" rel="noreferrer" className="hover:text-highlight">LinkedIn ↗</a>
      </div>
    </footer>
  );
}

export function Eyebrow({ children, tone = "light" }: { children: ReactNode; tone?: "light" | "dark" }) {
  return (
    <p className={`flex items-center gap-3 font-mono text-xs uppercase tracking-[0.3em] ${tone === "dark" ? "text-tint" : "text-highlight"}`}>
      <span className={`h-px w-8 ${tone === "dark" ? "bg-tint" : "bg-primary"}`} />
      {children}
    </p>
  );
}

export function PageHead({ code, title, sub, image, alt = "" }: { code: string; title: string; sub: string; image?: string; alt?: string }) {
  return (
    <section className="relative overflow-hidden bg-navy text-navy-foreground">
      {image && <img src={image} alt={alt} width={1920} height={1088} className="absolute inset-0 h-full w-full object-cover object-[75%_center]" />}
      <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/80 to-navy/20" />
      <div className="absolute inset-0 bg-cover bg-center opacity-[0.07] mix-blend-screen" style={{ backgroundImage: `url(${chart})` }} />
      <div className="blueprint absolute inset-0" />
      <div className="relative mx-auto max-w-7xl px-6 py-24 md:py-28">
        <Eyebrow tone="dark">{code}</Eyebrow>
        <h1 className="mt-6 max-w-4xl font-display text-5xl uppercase leading-[0.95] md:text-7xl">{title}</h1>
        <p className="mt-6 max-w-2xl text-lg text-navy-foreground/85">{sub}</p>
      </div>
    </section>
  );
}

export const capabilities = [
  {
    code: "CAP-01",
    title: "Business Development Recon",
    body: "We create and qualify leads, innovate the way we network, and target strategic partnerships that result in new and sustained business — seeking out new strategies, targets, companies, and prospects that deliver long-term value.",
    points: ["Lead generation & qualification", "Strategic partner targeting", "Capture strategy & positioning"],
  },
  {
    code: "CAP-02",
    title: "OSINT Market Intelligence",
    body: "Market assessments verified across multiple sources. Using open-source intelligence — social monitoring, conference presence, and direct networking with government and industry — we sit in the channels that reveal what customers actually need.",
    points: ["Open-source intelligence monitoring", "Government & industry networks", "Multi-source verified analysis"],
  },
  {
    code: "CAP-03",
    title: "Rapid Prototyping & Deployment",
    body: "Proprietary, military, and industry-standard processes to evaluate, plan, develop, test, and rapidly deploy the capabilities our clients need in the field.",
    points: ["Requirements to prototype", "Test & evaluation", "Accelerated R&D transition"],
  },
];

export function Shell({ children }: { children: ReactNode }) {
  return (
    <div className="public-site reimagined min-h-screen bg-background text-foreground">
      <Header />
      <main>{children}</main>
      <Footer />
    </div>
  );
}
